/**
 * PocketFiler - Create Document Page
 * Rich text document creator with AI assistance and export.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Heading1, Heading2, Heading3,
  Undo2, Redo2, Download, Save, Sparkles, Loader2,
  ArrowLeft, Type, Minus, Link as LinkIcon, Image as ImageIcon, Languages, Printer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';


// ============================================
// TOOLBAR BUTTON
// ============================================

function ToolbarButton({ icon: Icon, label, onClick, active }) {
  return (
    <button
      type="button"
      title={label}
      onClick={onClick}
      className={`p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
        active ? 'bg-gray-200 dark:bg-gray-700 text-primary' : 'text-gray-600 dark:text-gray-400'
      }`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

function ToolbarSeparator() {
  return <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />;
}


// ============================================
// MAIN PAGE
// ============================================

export default function CreateDocumentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const editorRef = useRef(null);

  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [docType, setDocType] = useState('general');
  const [translateOpen, setTranslateOpen] = useState(false);
  const [targetLang, setTargetLang] = useState('es');
  const [translating, setTranslating] = useState(false);

  // Auto-save to localStorage every 10 seconds
  const DRAFT_KEY = 'pocketfiler_doc_draft';
  
  // Load draft on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.title) setTitle(draft.title);
        if (draft.docType) setDocType(draft.docType);
        if (draft.content && editorRef.current) {
          editorRef.current.innerHTML = draft.content;
        }
      }
    } catch (e) {}
  }, []);

  // Auto-save every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const content = editorRef.current?.innerHTML || '';
        if (title || (content && content !== '<br>')) {
          localStorage.setItem(DRAFT_KEY, JSON.stringify({
            title,
            docType,
            content,
            savedAt: new Date().toISOString(),
          }));
        }
      } catch (e) {}
    }, 10000);
    return () => clearInterval(interval);
  }, [title, docType]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      const content = editorRef.current?.innerHTML || '';
      if (title || (content && content !== '<br>')) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [title]);

  // Clear draft after successful save
  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch(e) {}
  };

  // Execute formatting command
  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  }, []);

  // Format block
  const formatBlock = useCallback((tag) => {
    document.execCommand('formatBlock', false, tag);
    editorRef.current?.focus();
  }, []);

  // Insert link
  const insertLink = () => {
    if (linkUrl.trim()) {
      execCommand('createLink', linkUrl);
      setLinkUrl('');
      setLinkOpen(false);
    }
  };

  // AI Generate content
  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setAiGenerating(true);
    try {
      const res = await api.post('/ai/generate', {
        prompt: aiPrompt,
        language: user?.preferred_language || 'en',
      });
      const html = res.data?.text || res.text || '';
      if (html && editorRef.current) {
        // Insert at cursor position
        document.execCommand('insertHTML', false, html);
        toast.success(t('createDoc.aiContentInserted') || 'AI content inserted');
      }
      setAiPrompt('');
      setAiOpen(false);
    } catch (e) {
      toast.error(t('createDoc.aiGenerationFailed') || 'AI generation failed');
    }
    setAiGenerating(false);
  };

  // Save document
  const handleSave = async () => {
    if (!title.trim()) {
      toast.error(t('createDoc.titleRequired') || 'Please enter a document title');
      return;
    }
    const content = editorRef.current?.innerHTML || '';
    if (!content.trim() || content === '<br>') {
      toast.error(t('createDoc.contentRequired') || 'Document cannot be empty');
      return;
    }

    setSaving(true);
    try {
      // Create a Blob from HTML content
      const fullHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${title}</title>
<style>body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;line-height:1.6;color:#333;max-width:800px;margin:40px auto;padding:0 20px}h1,h2,h3{color:#1a1a1a}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ddd;padding:8px;text-align:left}</style>
</head><body>${content}</body></html>`;

      const blob = new Blob([fullHtml], { type: 'text/html' });
      const file = new File([blob], `${title.replace(/[^a-zA-Z0-9]/g, '_')}.html`, { type: 'text/html' });

      // Get presigned URL
      const presignRes = await api.post('/documents/presigned-upload', {
        filename: file.name,
        content_type: 'text/html',
        size: file.size,
        doc_type: docType,
      });

      const { upload_url, document_id } = presignRes.data || presignRes;

      if (upload_url) {
        // Upload to S3
        await fetch(upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': 'text/html' },
        });

        // Register the document
        await api.post('/documents/register-upload', {
          document_id,
          original_filename: file.name,
          display_name: title,
          doc_type: docType,
        });
      }

      clearDraft();
      toast.success(t('createDoc.saved') || 'Document saved successfully');
      navigate('/documents');
    } catch (e) {
      console.error('Save failed:', e);
      toast.error(t('createDoc.saveFailed') || 'Failed to save document');
    }
    setSaving(false);
  };



  // Translate entire document
  const handleTranslate = async () => {
    const content = editorRef.current?.innerHTML || '';
    if (!content.trim() || content === '<br>') {
      toast.error(t('createDoc.contentRequired') || 'Document cannot be empty');
      return;
    }
    setTranslating(true);
    try {
      const res = await api.post('/ai/translate-document', {
        content,
        target_language: targetLang,
      });
      const translated = res.data?.translated || res.translated || '';
      if (translated && editorRef.current) {
        editorRef.current.innerHTML = translated;
        toast.success(t('createDoc.translated') || 'Document translated successfully');
      }
      setTranslateOpen(false);
    } catch (e) {
      toast.error(t('createDoc.translateFailed') || 'Translation failed');
    }
    setTranslating(false);
  };

  // Export as HTML file download
  const handleExport = () => {
    const content = editorRef.current?.innerHTML || '';
    const fullHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${title || 'Untitled Document'}</title>
<style>body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;line-height:1.6;color:#333;max-width:800px;margin:40px auto;padding:0 20px}h1,h2,h3{color:#1a1a1a}</style>
</head><body>${content}</body></html>`;

    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(title || 'untitled').replace(/[^a-zA-Z0-9]/g, '_')}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Top bar */}
      <div className="border-b px-6 py-3 flex items-center justify-between bg-white dark:bg-gray-950">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/documents')}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('createDoc.untitledDocument') || 'Untitled Document'}
            className="border-0 text-lg font-semibold bg-transparent focus-visible:ring-0 w-64 md:w-96 px-0"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="general">{t('createDoc.typeGeneral') || 'General'}</SelectItem>
              <SelectItem value="contract">{t('createDoc.typeContract') || 'Contract'}</SelectItem>
              <SelectItem value="invoice">{t('createDoc.typeInvoice') || 'Invoice'}</SelectItem>
              <SelectItem value="letter">{t('createDoc.typeLetter') || 'Letter'}</SelectItem>
              <SelectItem value="report">{t('createDoc.typeReport') || 'Report'}</SelectItem>
              <SelectItem value="other">{t('createDoc.typeOther') || 'Other'}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => window.print()} className="gap-1.5 no-print">
            <Printer className="h-3.5 w-3.5" />
            {t('createDoc.print') || 'Print'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} className="gap-1.5 no-print">
            <Download className="h-3.5 w-3.5" />
            {t('createDoc.export') || 'Export'}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {t('createDoc.save') || 'Save'}
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="border-b px-6 py-1.5 flex items-center gap-0.5 flex-wrap bg-gray-50 dark:bg-gray-900">
        <ToolbarButton icon={Bold} label="Bold" onClick={() => execCommand('bold')} />
        <ToolbarButton icon={Italic} label="Italic" onClick={() => execCommand('italic')} />
        <ToolbarButton icon={Underline} label="Underline" onClick={() => execCommand('underline')} />
        <ToolbarSeparator />
        <ToolbarButton icon={Heading1} label="Heading 1" onClick={() => formatBlock('h1')} />
        <ToolbarButton icon={Heading2} label="Heading 2" onClick={() => formatBlock('h2')} />
        <ToolbarButton icon={Heading3} label="Heading 3" onClick={() => formatBlock('h3')} />
        <ToolbarButton icon={Type} label="Paragraph" onClick={() => formatBlock('p')} />
        <ToolbarSeparator />
        <ToolbarButton icon={AlignLeft} label="Align Left" onClick={() => execCommand('justifyLeft')} />
        <ToolbarButton icon={AlignCenter} label="Align Center" onClick={() => execCommand('justifyCenter')} />
        <ToolbarButton icon={AlignRight} label="Align Right" onClick={() => execCommand('justifyRight')} />
        <ToolbarSeparator />
        <ToolbarButton icon={List} label="Bullet List" onClick={() => execCommand('insertUnorderedList')} />
        <ToolbarButton icon={ListOrdered} label="Numbered List" onClick={() => execCommand('insertOrderedList')} />
        <ToolbarSeparator />
        <ToolbarButton icon={LinkIcon} label="Insert Link" onClick={() => setLinkOpen(true)} />
        <ToolbarButton icon={Minus} label="Horizontal Rule" onClick={() => execCommand('insertHorizontalRule')} />
        <ToolbarSeparator />
        <ToolbarButton icon={Undo2} label="Undo" onClick={() => execCommand('undo')} />
        <ToolbarButton icon={Redo2} label="Redo" onClick={() => execCommand('redo')} />
        <ToolbarSeparator />
        <button
          onClick={() => setAiOpen(true)}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-xs font-medium"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {t('createDoc.aiAssist') || 'AI Assist'}
        </button>
        <button
          onClick={() => setTranslateOpen(true)}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-400 dark:hover:bg-blue-900 transition-colors text-xs font-medium"
        >
          <Languages className="h-3.5 w-3.5" />
          {t('createDoc.translate') || 'Translate'}
        </button>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-950 p-6">
        <div className="max-w-[816px] mx-auto bg-white dark:bg-gray-900 shadow-sm rounded-lg min-h-[1056px] border">
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="p-12 min-h-[1056px] outline-none prose prose-sm dark:prose-invert max-w-none"
            style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", lineHeight: '1.6' }}
            onPaste={(e) => {
              // Clean paste — strip most formatting but keep basic structure
              e.preventDefault();
              const html = e.clipboardData.getData('text/html');
              const text = e.clipboardData.getData('text/plain');
              if (html) {
                // Allow basic HTML tags only
                const clean = html.replace(/<(?!\/?(p|br|b|i|u|strong|em|h[1-3]|ul|ol|li|a|table|tr|td|th|thead|tbody|hr)\b)[^>]*>/gi, '');
                document.execCommand('insertHTML', false, clean);
              } else {
                document.execCommand('insertText', false, text);
              }
            }}
          />
        </div>
      </div>

      {/* AI Assist Dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {t('createDoc.aiAssistTitle') || 'AI Writing Assistant'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>{t('createDoc.aiPromptLabel') || 'What would you like AI to write?'}</Label>
            <Textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder={t('createDoc.aiPromptPlaceholder') || 'e.g., Write an introduction paragraph for a service agreement between...'}
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              {t('createDoc.aiPromptHint') || 'AI will generate content and insert it at your cursor position.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)}>
              {t('createDoc.cancel') || 'Cancel'}
            </Button>
            <Button onClick={handleAiGenerate} disabled={aiGenerating || !aiPrompt.trim()} className="gap-1.5">
              {aiGenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {aiGenerating ? (t('createDoc.generating') || 'Generating...') : (t('createDoc.generate') || 'Generate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Translate Dialog */}
      <Dialog open={translateOpen} onOpenChange={setTranslateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5 text-blue-600" />
              {t('createDoc.translateTitle') || 'Translate Document'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>{t('createDoc.translateTo') || 'Translate to'}</Label>
            <Select value={targetLang} onValueChange={setTargetLang}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="de">Deutsch</SelectItem>
                <SelectItem value="pt">Português</SelectItem>
                <SelectItem value="ar">العربية</SelectItem>
                <SelectItem value="hi">हिन्दी</SelectItem>
                <SelectItem value="ja">日本語</SelectItem>
                <SelectItem value="ko">한국어</SelectItem>
                <SelectItem value="ru">Русский</SelectItem>
                <SelectItem value="zh-CN">中文</SelectItem>
                <SelectItem value="vi">Tiếng Việt</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t('createDoc.translateHint') || 'This will replace the current document content with the translated version. Use Ctrl+Z to undo.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTranslateOpen(false)}>
              {t('createDoc.cancel') || 'Cancel'}
            </Button>
            <Button onClick={handleTranslate} disabled={translating} className="gap-1.5">
              {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Languages className="h-3.5 w-3.5" />}
              {translating ? (t('createDoc.translating') || 'Translating...') : (t('createDoc.translateNow') || 'Translate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('createDoc.insertLink') || 'Insert Link'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>URL</Label>
            <Input
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://..."
              onKeyDown={(e) => e.key === 'Enter' && insertLink()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>
              {t('createDoc.cancel') || 'Cancel'}
            </Button>
            <Button onClick={insertLink}>
              {t('createDoc.insert') || 'Insert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
